import { PaletteMode, BreakpointsOptions, Direction, createTheme } from '@mui/material';
import { flowMixins } from './mixins';
import { flowAccordion } from '../accordion/accordion.theme';
import { flowAlert } from '../alert/alert.theme';
import { flowAlertTitle } from '../alert/alertTitle.theme';
import { flowAvatar } from '../avatar/avatar.theme';
import { flowAvatarGroup } from '../avatar/avatarGroup.theme';
import { flowAccordionSummary } from '../accordion/accordionSummary.theme';
import { flowAccordionDetails } from '../accordion/accordionDetails.theme';
import { flowAccordionActions } from '../accordion/accordionActions.theme';
import { flowAppBar } from '../appbar/appbar.theme';
import { flowToolbar } from '../appbar/toolbar.theme';
import { flowAutocomplete } from '../autocomplete/autocomplete.theme';
import { flowBackdrop } from '../backdrop/backdrop.theme';
import { flowBadge } from '../badge/badge.theme';
import { getFlowScopedCssBaseline, getFlowCssBaseline } from '../themeProvider/baseline.theme';
import { flowBreadCrumbs } from '../breadcrumbs/breadcrumbs.theme';
import { flowButton } from '../button/button.theme';
import { flowButtonBase } from '../button/buttonBase.theme';
import { flowLink } from '../link/link.theme';
import { flowCheckbox } from '../checkbox/checkbox.theme';
import { flowChip } from '../chip/chip.theme';
import { flowDialog } from '../dialog/dialog.theme';
import { flowDialogActions } from '../dialog/dialogActions.theme';
import { flowDialogContent } from '../dialog/dialogContent.theme';
import { flowDialogContentText } from '../dialog/dialogContentText.theme';
import { flowDialogTitle } from '../dialog/dialogTitle.theme';
import { flowDivider } from '../divider/divider.theme';
import { flowDrawer } from '../drawer/drawer.theme';
import { flowFilledInput } from '../textfield/filledInput.theme';
import { flowOutlinedInput } from '../textfield/outlinedInput.theme';
import { flowFormControl } from '../textfield/formControl.theme';
import { flowFormControlLabel } from '../textfield/formControlLabel.theme';
import { flowFormHelperText } from '../textfield/formHelperText.theme';
import { flowFormLabel } from '../textfield/formLabel.theme';
import { flowInput } from '../textfield/input.theme';
import { flowInputAdornment } from '../textfield/inputAdornment.theme';
import { flowInputBase } from '../textfield/inputBase.theme';
import { flowInputLabel } from '../textfield/inputLabel.theme';
import { flowRadio } from '../radio/radio.theme';
import { flowSelect } from '../textfield/select.theme';
import { flowSlider } from '../slider/slider.theme';
import { flowSwitch } from '../switch/switch.theme';
import { flowTextField } from '../textfield/textField.theme';
import { flowSvgIcon } from '../svgIcon/svgIcon.theme';
import { flowIconButton } from '../iconButton/iconButton.theme';
import { flowLinearProgress } from '../linearprogress/linearprogress.theme';
import { flowCircularProgress } from '../circularprogress/circularprogress.theme';
import { flowContainer } from '../container/container.theme';
import { flowList } from '../list/list.theme';
import { flowListItem } from '../list/listItem.theme';
import { flowListItemButton } from '../list/listItemButton.theme';
import { flowListItemText } from '../list/listItemText.theme';
import { flowListItemIcon } from '../list/listItemIcon.theme';
import { flowListItemAvatar } from '../list/listItemAvatar.theme';
import { flowListItemSecondaryAction } from '../list/listItemSecondaryAction.theme';
import { flowListSubheader } from '../list/listSubheader.theme';
import { flowMenu } from '../menu/menu.theme';
import { flowMenuItem } from '../menu/menuItem.theme';
import { getFlowModal } from '../modal/modal.theme';
import { flowPagination } from '../pagination/pagination.theme';
import { flowPaginationItem } from '../pagination/paginationItem.theme';
import { flowPaper } from '../paper/paper.theme';
import { flowPopover } from '../popover/popover.theme';
import { flowRating } from '../rating/rating.theme';
import { flowSkeleton } from '../skeleton/skeleton.theme';
import { flowSnackbar } from '../snackbar/snackbar.theme';
import { flowSnackbarContent } from '../snackbar/snackbarContent.theme';
import { flowStepper } from '../stepper/stepper.theme';
import { flowStep } from '../stepper/step.theme';
import { flowStepButton } from '../stepper/stepButton.theme';
import { flowStepConnector } from '../stepper/stepConnector.theme';
import { flowStepLabel } from '../stepper/stepLabel.theme';
import { flowStepContent } from '../stepper/stepContent.theme';
import { flowTab } from '../tabs/tab.theme';
import { flowTabs } from '../tabs/tabs.theme';
import { flowTooltip } from '../tooltip/tooltip.theme';
import { flowCard } from '../card/card.theme';
import { flowCardContent } from '../card/cardContent.theme';
import { flowCardHeader } from '../card/cardHeader.theme';
import { flowCardActionArea } from '../card/cardActionArea.theme';
import { flowTableContainer } from '../table/tableContainer.theme';
import { flowTable } from '../table/table.theme';
import { flowTableHead } from '../table/tableHead.theme';
import { flowTableBody } from '../table/tableBody.theme';
import { flowTableRow } from '../table/tableRow.theme';
import { flowTableCell } from '../table/tableCell.theme';
import { flowTableSortLabel } from '../table/tableSortLabel.theme';
import { flowTablePagination } from '../table/tablePagination.theme';
import { flowFab } from '../fab/fab.theme';
import { paletteLight } from './paletteLight.theme';
import { paletteDark } from './paletteDark.theme';
import { flowBreakpoints } from './theme.breakpoints';
import { flowElevations } from './theme.elevations';
import { flowZIndex } from './theme.zIndex';
import { flowDatePicker, flowDesktopDatePicker, flowStaticDatePicker } from '../datepicker/datepicker.theme';
import { flowTimePicker, flowDesktopTimePicker, flowStaticTimePicker } from '../datepicker/timepicker.theme';
import {
  flowDateTimePicker,
  flowDesktopDateTimePicker,
  flowStaticDateTimePicker,
} from '../datepicker/datetimepicker.theme';
import { flowDateCalendar } from '../datepicker/datecalendar.theme';
import {
  flowPickersFilledInput,
  flowPickersInput,
  flowPickersInputBase,
  flowPickersOutlinedInput,
  flowPickersTextField,
} from '../datepicker/pickerstextfield.theme';
import { flowPickerPopper } from '../datepicker/pickerpopper.theme';
import { flowPickersLayout } from '../datepicker/pickerslayout.theme';
import { flowPickersDay } from '../datepicker/pickersday.theme';
import { flowDayCalendar } from '../datepicker/daycalendar.theme';
import { flowMonthCalendar } from '../datepicker/monthcalendar.theme';
import { flowYearCalendar } from '../datepicker/yearcalendar.theme';
import { flowTimeClock } from '../datepicker/timeclock.theme';
import { flowDigitalClock } from '../datepicker/digitalclock.theme';
import { flowMultiSectionDigitalClock } from '../datepicker/multisectiondigitalclock.theme';
import { flowMobileDatePicker } from '../datepicker/mobiledatepicker.theme';
import { flowMobileTimePicker } from '../datepicker/mobiletimepicker.theme';
import { flowMobileDateTimePicker } from '../datepicker/mobiledatetimepicker.theme';
import { flowDatePickerToolbar } from '../datepicker/datepickertoolbar.theme';
import { flowTimePickerToolbar } from '../datepicker/timepickertoolbar.theme';
import { flowDateTimePickerToolbar } from '../datepicker/datetimepickertoolbar.theme';
import './theme.extensions';
import { getTypographyOptions } from '../typography/typography.theme';
import '@mui/x-date-pickers/themeAugmentation';

export function createSkodaTheme({
  mode = 'light',
  direction = 'ltr',
  breakpointsOptions = flowBreakpoints,
}: {
  mode?: PaletteMode;
  direction?: Direction;
  breakpointsOptions?: BreakpointsOptions;
}) {
  return createTheme({
    breakpoints: breakpointsOptions,
    mixins: flowMixins,
    palette: mode === 'dark' ? paletteDark : paletteLight,
    direction: direction,
    typography: getTypographyOptions(breakpointsOptions, direction === 'rtl'),
    flowShadows: flowElevations,
    zIndex: flowZIndex,
    components: {
      MuiAlert: flowAlert,
      MuiAlertTitle: flowAlertTitle,
      MuiAvatar: flowAvatar,
      MuiAvatarGroup: flowAvatarGroup,
      MuiAppBar: flowAppBar,
      MuiToolbar: flowToolbar,
      MuiContainer: flowContainer,
      MuiCssBaseline: getFlowCssBaseline(breakpointsOptions, direction === 'rtl'),
      MuiScopedCssBaseline: getFlowScopedCssBaseline(breakpointsOptions, direction === 'rtl'),
      MuiButtonBase: flowButtonBase,
      MuiButton: flowButton,
      MuiIconButton: flowIconButton,
      MuiSvgIcon: flowSvgIcon,
      MuiLink: flowLink,
      MuiLinearProgress: flowLinearProgress,
      MuiCircularProgress: flowCircularProgress,
      MuiSwitch: flowSwitch,
      MuiCheckbox: flowCheckbox,
      MuiRadio: flowRadio,
      MuiSlider: flowSlider,
      MuiTooltip: flowTooltip,
      MuiDivider: flowDivider,
      MuiBadge: flowBadge,
      MuiChip: flowChip,
      MuiPaper: flowPaper,
      MuiTextField: flowTextField,
      MuiSelect: flowSelect,
      MuiMenu: flowMenu,
      MuiMenuItem: flowMenuItem,
      MuiTab: flowTab,
      MuiTabs: flowTabs,
      MuiCard: flowCard,
      MuiCardContent: flowCardContent,
      MuiCardHeader: flowCardHeader,
      MuiCardActionArea: flowCardActionArea,
      MuiPaginationItem: flowPaginationItem,
      MuiPagination: flowPagination,
      MuiRating: flowRating,
      MuiSnackbar: flowSnackbar,
      MuiSnackbarContent: flowSnackbarContent,
      MuiModal: getFlowModal(breakpointsOptions, direction === 'rtl'),
      MuiBackdrop: flowBackdrop,
      MuiDrawer: flowDrawer,
      MuiAccordion: flowAccordion,
      MuiAccordionSummary: flowAccordionSummary,
      MuiAccordionDetails: flowAccordionDetails,
      MuiAccordionActions: flowAccordionActions,
      MuiInputBase: flowInputBase,
      MuiInputLabel: flowInputLabel,
      MuiInput: flowInput,
      MuiFormControl: flowFormControl,
      MuiFilledInput: flowFilledInput,
      MuiOutlinedInput: flowOutlinedInput,
      MuiFormLabel: flowFormLabel,
      MuiFormControlLabel: flowFormControlLabel,
      MuiInputAdornment: flowInputAdornment,
      MuiFormHelperText: flowFormHelperText,
      MuiDialogActions: flowDialogActions,
      MuiDialog: flowDialog,
      MuiDialogContent: flowDialogContent,
      MuiDialogContentText: flowDialogContentText,
      MuiDialogTitle: flowDialogTitle,
      MuiList: flowList,
      MuiListItem: flowListItem,
      MuiListItemButton: flowListItemButton,
      MuiListItemText: flowListItemText,
      MuiListItemIcon: flowListItemIcon,
      MuiListItemAvatar: flowListItemAvatar,
      MuiListItemSecondaryAction: flowListItemSecondaryAction,
      MuiListSubheader: flowListSubheader,
      MuiPopover: flowPopover,
      MuiAutocomplete: flowAutocomplete,
      MuiBreadcrumbs: flowBreadCrumbs,
      MuiSkeleton: flowSkeleton,
      MuiStepper: flowStepper,
      MuiStep: flowStep,
      MuiStepButton: flowStepButton,
      MuiStepConnector: flowStepConnector,
      MuiStepLabel: flowStepLabel,
      MuiStepContent: flowStepContent,
      MuiTableContainer: flowTableContainer,
      MuiTable: flowTable,
      MuiTableHead: flowTableHead,
      MuiTableBody: flowTableBody,
      MuiTableRow: flowTableRow,
      MuiTableCell: flowTableCell,
      MuiTableSortLabel: flowTableSortLabel,
      MuiTablePagination: flowTablePagination,
      MuiFab: flowFab,
      MuiDatePicker: flowDatePicker,
      MuiTimePicker: flowTimePicker,
      MuiDateTimePicker: flowDateTimePicker,
      MuiDateCalendar: flowDateCalendar,
      MuiPickersTextField: flowPickersTextField,
      MuiPickersInputBase: flowPickersInputBase,
      MuiPickersInput: flowPickersInput,
      MuiPickersOutlinedInput: flowPickersOutlinedInput,
      MuiPickersFilledInput: flowPickersFilledInput,
      MuiPickerPopper: flowPickerPopper,
      MuiPickersDay: flowPickersDay,
      MuiDayCalendar: flowDayCalendar,
      MuiMonthCalendar: flowMonthCalendar,
      MuiYearCalendar: flowYearCalendar,
      MuiTimeClock: flowTimeClock,
      MuiDigitalClock: flowDigitalClock,
      MuiMultiSectionDigitalClock: flowMultiSectionDigitalClock,
      MuiMobileDatePicker: flowMobileDatePicker,
      MuiMobileDateTimePicker: flowMobileDateTimePicker,
      MuiMobileTimePicker: flowMobileTimePicker,
      MuiDatePickerToolbar: flowDatePickerToolbar,
      MuiDateTimePickerToolbar: flowDateTimePickerToolbar,
      MuiTimePickerToolbar: flowTimePickerToolbar,
      MuiDesktopDatePicker: flowDesktopDatePicker,
      MuiDesktopTimePicker: flowDesktopTimePicker,
      MuiDesktopDateTimePicker: flowDesktopDateTimePicker,
      MuiStaticDatePicker: flowStaticDatePicker,
      MuiStaticTimePicker: flowStaticTimePicker,
      MuiStaticDateTimePicker: flowStaticDateTimePicker,
      MuiPickersLayout: flowPickersLayout,
    },
  });
}

export const theme = createSkodaTheme({});
export const darkTheme = createSkodaTheme({ mode: 'dark' });
export const rtlTheme = createSkodaTheme({ direction: 'rtl' });
export const rtlDarkTheme = createSkodaTheme({ direction: 'rtl', mode: 'dark' });
